/** @jsxImportSource @emotion/react */
import { memo, useState } from "react";
import { ProjectTablesTableColumns } from "./ProjectTablesTable";
import { css } from "@emotion/react";
import {
  EuiTableRowCell,
  EuiCheckbox,
  EuiIcon,
  EuiLink,
  EuiTableRow,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { ProjectTablesTableActionMenu } from "./ProjectTablesTableActionMenu";
import { DEFAULT_FOLDER_COLOR } from "./ProjectTablesTable.helpers";
import { useProjectTablesTableContext } from "components/ProjectTablesTable/provider/ProjectTablesTableProvider";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { isUndefined } from "lodash";
import { isNonNull } from "utils/isNonNull";
import { ProjectTablesFolderActionMenu } from "./ProjectTablesFolderActionMenu";
import { ProjectFolderDjango } from "pages/gdt/folders/ProjectFoldersProvider.api";
import { isDefined } from "utils/isDefined";
import {
  ProjectDataTable,
  ProjectTablesTableColumn,
} from "./ProjectTablesTable.types";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { useDndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { uuid } from "utils/uuid";
import {
  ProjectTablesDraggableData,
  ProjectTablesDroppableData,
} from "./ProjectTablesDndProvider";
import { TableIcon } from "./TableIcon";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";

const ProjectTablesTableRowCell = memo(function ProjectTablesTableRowCell({
  table,
  column,
  isSelected,
}: {
  table: ProjectDataTable;
  column: ProjectTablesTableColumn;
  isSelected: boolean;
}) {
  const selectTable = useProjectTablesTableContext(
    (state) => state.selectTable,
  );
  const selectRangeOfTables = useProjectTablesTableContext(
    (state) => state.selectRangeOfTables,
  );

  // const navigateToTable = useDataTableRouting();
  const projectKey = useDataTableContext((s) => s.project.key);
  const tenantKey = useDataTableContext((s) => s.project.tenant.key);
  const navigateToTable = useRouteMapContext(
    (s) => (tableKey: string) =>
      s.routeMap["PROJECT_TABLE"]
        .dynamicPath({ projectKey, tableKey, tenantKey })
        .navigateTo(),
  );
  const editable = useProjectPermissionStore((s) => s.permissions.edit);

  const isInFolder = table.folder != null;

  const [draggableId] = useState(uuid);

  const data: ProjectTablesDraggableData = {
    tableId: table.id,
  };

  const theme = useEuiTheme();

  const padding = theme.euiTheme.size.s;

  const { active } = useDndContext();

  const { listeners, attributes, setNodeRef } = useDraggable({
    id: draggableId,
    data,
    disabled: !editable,
  });

  // render actions menu
  if (column.isActionsPopover) {
    return (
      <ProjectTablesTableActionMenu
        key={`${table.id}-${column.id}`}
        column={column}
        table={table}
      />
    );
  }

  // render name column with selection controls
  if (column.id === "name") {
    return (
      <EuiTableRowCell
        key={`${table.id}-${column.id}`}
        textOnly={false}
        hasActions={false}
        align={column.alignment}
        className={isSelected ? "selected" : ""}
        css={
          isInFolder
            ? css`
                padding-left: 24px !important;
              `
            : undefined
        }
      >
        <span
          {...listeners}
          {...attributes}
          ref={setNodeRef}
          className={"table-draggable"}
          css={css`
            padding: ${padding};
            margin: -${padding};
            width: 100%;
            box-sizing: border-box;
          `}
        >
          <EuiCheckbox
            id={`checkbox-${table.id}`}
            checked={isSelected ?? false}
            onClick={(e) => {
              e.stopPropagation();
              // handle range selection on shift click
              if (e.shiftKey) {
                selectRangeOfTables(table.id);
              }
            }}
            onChange={(e) => {
              selectTable(table.id, e.target.checked);
            }}
            className={`table-checkbox ${
              active !== null ? "table-dragging" : ""
            }`}
          />
          <TableIcon tableType={table.kind} />{" "}
          <EuiLink
            onClick={() => {
              // navigate to table
              navigateToTable(table.key);
            }}
          >
            <span
              css={css`
                color: ${DEFAULT_FOLDER_COLOR} !important;
              `}
            >
              {table.name}{" "}
            </span>
            <span
              css={css`
                color: #ccc !important;
              `}
            >
              ({table.key})
            </span>
          </EuiLink>
        </span>
      </EuiTableRowCell>
    );
  }
  // render all other columns that provide a render method
  if (column.render) {
    return (
      <EuiTableRowCell
        key={column.id}
        textOnly={false}
        hasActions={false}
        align={column.alignment}
        onClick={() => {
          navigateToTable(table.key);
        }}
        style={{ cursor: "pointer" }}
      >
        {column.render(table)}
      </EuiTableRowCell>
    );
  }

  // render all other columns
  return (
    <EuiTableRowCell
      key={column.id}
      align={column.alignment}
      textOnly={column.textOnly || false}
      onClick={() => {
        navigateToTable(table.key);
      }}
      style={{ cursor: "pointer" }}
    >
      {table[column.id as keyof ProjectDataTable]}
    </EuiTableRowCell>
  );
});

const ProjectTablesTableRow = memo(function DataTablesTableRow({
  table,
}: {
  table: ProjectDataTable;
}) {
  const visibleColumnsMap = useProjectTablesTableContext(
    (state) => state.visibleColumnsMap,
  );
  const selectedTableMap = useProjectTablesTableContext(
    (state) => state.selectedTableMap,
  );
  return (
    <EuiTableRow
      key={table.id}
      hasActions={true}
      className={`table-row ${table.hidden ? "hidden" : ""}`}
    >
      {ProjectTablesTableColumns.filter(
        (column) => visibleColumnsMap[column.id],
      ).map((column: ProjectTablesTableColumn) => {
        const isSelected = selectedTableMap[table.id] ?? false;
        return (
          <ProjectTablesTableRowCell
            key={column.id}
            column={column}
            table={table}
            isSelected={isSelected}
          />
        );
      })}
    </EuiTableRow>
  );
});

const ProjectTablesFolderRowCell = memo(function DataTablesFolderRowCell({
  column,
  folder,
  isOpen,
  folderTableRowCount,
}: {
  column: ProjectTablesTableColumn;
  folder: ProjectFolderDjango;
  isOpen: boolean;
  folderTableRowCount: number;
}) {
  const theme = useEuiTheme();

  const [id] = useState(uuid);

  const data: ProjectTablesDroppableData = {
    folderId: folder.id,
  };

  const editable = useProjectPermissionStore((s) => s.permissions.edit);
  const { setNodeRef, isOver } = useDroppable({
    id,
    data,
    disabled: !editable,
  });

  const toggleFolderExpanded = useProjectTablesTableContext(
    (state) => state.toggleFolderExpanded,
  );

  // render actions menu
  if (column.isActionsPopover) {
    return (
      <ProjectTablesFolderActionMenu
        key={column.id}
        column={column}
        folder={folder}
      />
    );
  }

  const padding = theme.euiTheme.size.s;

  // render file name with color, click to expand/collapse
  if (column.id === "name") {
    return (
      <EuiTableRowCell
        key={column.id}
        textOnly={false}
        hasActions={false}
        align={column.alignment}
      >
        <div
          ref={setNodeRef}
          css={css`
            padding: ${padding};
            margin: -${padding};
          `}
          style={{
            width: "100%",
            border: isOver ? "2px dashed lightgray" : "none",
            borderRadius: 5,
          }}
        >
          <span
            onClick={() => toggleFolderExpanded(folder.id)}
            css={css`
              cursor: pointer;
              color: ${isNonNull(folder.color)
                ? folder.color
                : DEFAULT_FOLDER_COLOR};
            `}
          >
            <EuiIcon
              type={isOpen ? "folderOpen" : "folderClosed"}
              size="m"
              style={{ verticalAlign: "text-top" }}
            />{" "}
            {folder.name}
          </span>
        </div>
      </EuiTableRowCell>
    );
  }
  // render no content for all other columns for folders
  return (
    <EuiTableRowCell
      key={column.id}
      align={column.alignment}
      textOnly={column.textOnly || false}
    ></EuiTableRowCell>
  );
});

const ProjectTablesFolderRow = memo(function DataTablesFolderRow({
  folder,
  isOpen,
  folderTableRows,
}: {
  folder: ProjectFolderDjango;
  isOpen: boolean;
  folderTableRows?: JSX.Element[];
}) {
  const isSearching = useProjectTablesTableContext(
    (state) => state.isSearching,
  );
  const visibleColumnsMap = useProjectTablesTableContext(
    (state) => state.visibleColumnsMap,
  );
  if (
    isSearching &&
    (isUndefined(folderTableRows) || folderTableRows.length === 0)
  ) {
    return null;
  }

  // don't show table rows for closed folders
  const folderTableDisplayedRows = isOpen ? folderTableRows : null;
  // if folder is open, display child table rows after the folder row
  return (
    <>
      <EuiTableRow key={folder.id} hasActions={true}>
        {ProjectTablesTableColumns.filter(
          (column) => visibleColumnsMap[column.id],
        ).map((column) => {
          return (
            <ProjectTablesFolderRowCell
              key={column.id}
              column={column}
              folder={folder}
              isOpen={isOpen}
              folderTableRowCount={folderTableRows?.length ?? 0}
            />
          );
        })}
      </EuiTableRow>
      {folderTableDisplayedRows}
    </>
  );
});

export const ProjectTablesTableRows = memo(function DataTablesTableRows() {
  const sortedAndFilteredTables = useProjectTablesTableContext(
    (state) => state.sortedAndFilteredTables,
  );
  const sortedColumn = useProjectTablesTableContext(
    (state) => state.sortedColumn,
  );
  const folders = useProjectTablesTableContext((state) => state.folders);
  const foldersExpandedMap = useProjectTablesTableContext(
    (state) => state.foldersExpandedMap,
  );

  // build a map of tables by folder
  const tablesByFolderId: Record<string, JSX.Element[]> = {};
  const tableRows = sortedAndFilteredTables
    .map((table) => {
      const tableRow = <ProjectTablesTableRow key={table.id} table={table} />;
      const folderId = table.folder;
      // if the table is in a folder, add it to the map and not to the list of table rows
      // it will get rendered with the folder row instead
      if (folderId != null) {
        tablesByFolderId[folderId] = [
          ...(isDefined(tablesByFolderId[folderId])
            ? tablesByFolderId[folderId]
            : []),
          tableRow,
        ];
        return undefined;
      }
      return tableRow;
    })
    // get rid of undefined entries that were saved to the folder map
    .filter(isDefined);

  // build folder rows (and their child table rows)
  const folderRows = [...folders]
    // sort the folders first - they will obey the sort order of the currently sorted column
    // but always sort by name as they have no other properties
    .sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      if (aName < bName) {
        return sortedColumn[0] === "name" && sortedColumn[1] === "DESC"
          ? 1
          : -1;
      }
      if (aName > bName) {
        return sortedColumn[0] === "name" && sortedColumn[1] === "DESC"
          ? -1
          : 1;
      }
      return 0;
    })
    .map((folder) => {
      // if the folder isn't in the map yet, assume open
      const isOpen = Object.prototype.hasOwnProperty.call(
        foldersExpandedMap,
        folder.id,
      )
        ? foldersExpandedMap[folder.id]
        : true;
      // get table rows
      const folderTableRows = tablesByFolderId[folder.id];
      return (
        <ProjectTablesFolderRow
          key={folder.id}
          folder={folder}
          isOpen={isOpen}
          folderTableRows={folderTableRows}
        />
      );
    });

  return (
    <>
      {folderRows}
      {tableRows}
    </>
  );
});
